import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2, Clock, AlertCircle, Loader2, Image as ImageIcon, FileText, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EvidenciaStatusBadge } from "@/components/EvidenciaStatusBadge";
import { EsiStatusBadge } from "@/components/EsiStatusBadge";
import { useEvidenciaStatusOptions } from "@/hooks/useEvidenciaStatusOptions";
import { useEsiStatusOptions } from "@/hooks/useEsiStatusOptions";
import { usePermission } from "@/hooks/usePermission";

function StatCard({ label, value, icon: Icon, tone = "primary", testId }) {
  const toneMap = {
    primary: "border-primary/40 bg-primary/5 text-primary",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-400",
    danger: "border-destructive/50 bg-destructive/10 text-destructive",
    muted: "border-border bg-muted/30 text-muted-foreground",
    success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  };
  return (
    <div className={`p-3 rounded-lg border ${toneMap[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 opacity-80" />}
      </div>
      <div className="text-2xl font-semibold font-mono tabular-nums">{value}</div>
    </div>
  );
}

export default function EvidenciaStatusTab({ ot, onUpdated }) {
  const qc = useQueryClient();
  const canChange = usePermission("evidencia_estatus");
  const canChangeEsi = usePermission("esi_estatus");
  const { options: STATUS_OPTIONS } = useEvidenciaStatusOptions();
  const { options: ESI_STATUS_OPTIONS } = useEsiStatusOptions();

  const [selectedStatus, setSelectedStatus] = useState(ot?.estatus_evidencia || "Pendiente de subir");
  const [saving, setSaving] = useState(false);
  const [selectedEsi, setSelectedEsi] = useState(ot?.estatus_esi || "Pendiente revisión");
  const [savingEsi, setSavingEsi] = useState(false);

  // Fetch evidencias to compute coverage
  const { data: evidencias = [], isLoading: loadingEv } = useQuery({
    queryKey: ["evidencias", ot?.id],
    queryFn: async () => (await api.get(`/ots/${ot.id}/evidencias`)).data,
    enabled: !!ot?.id,
  });

  // Fetch audit history filtered to evidencia_status_changed events
  const { data: audit = [] } = useQuery({
    queryKey: ["audit", ot?.id, "evidencia"],
    queryFn: async () => (await api.get(`/ots/${ot.id}/history`)).data,
    enabled: !!ot?.id,
    select: (rows) => (rows || []).filter((r) => r.action === "evidencia_status_changed"),
  });

  // Fetch audit history filtered to esi_status_changed events
  const { data: auditEsi = [] } = useQuery({
    queryKey: ["audit", ot?.id, "esi"],
    queryFn: async () => (await api.get(`/ots/${ot.id}/history`)).data,
    enabled: !!ot?.id,
    select: (rows) => (rows || []).filter((r) => r.action === "esi_status_changed"),
  });

  const summary = useMemo(() => {
    const items = ot?.items || [];
    const skus = Array.from(new Set(items.map((i) => i.sku).filter(Boolean)));
    const evBySku = new Set(evidencias.map((e) => e.sku).filter(Boolean));
    const withEv = skus.filter((s) => evBySku.has(s)).length;
    const withoutEv = skus.length - withEv;
    return {
      totalFiles: evidencias.length,
      totalSkus: skus.length,
      withEv,
      withoutEv,
      coverage: skus.length > 0 ? Math.round((withEv / skus.length) * 100) : 0,
    };
  }, [ot, evidencias]);

  const save = async () => {
    if (!selectedStatus || selectedStatus === ot?.estatus_evidencia) {
      toast.info("No hay cambios que guardar");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/ots/${ot.id}/estatus-evidencia`, { estatus_evidencia: selectedStatus });
      toast.success(`Estatus de evidencia actualizado a "${selectedStatus}"`);
      qc.invalidateQueries({ queryKey: ["ot", ot.id] });
      qc.invalidateQueries({ queryKey: ["audit", ot.id, "evidencia"] });
      qc.invalidateQueries({ queryKey: ["ots-list"] });
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar estatus");
    } finally {
      setSaving(false);
    }
  };

  const saveEsi = async () => {
    if (!selectedEsi || selectedEsi === ot?.estatus_esi) {
      toast.info("No hay cambios que guardar");
      return;
    }
    setSavingEsi(true);
    try {
      await api.patch(`/ots/${ot.id}/estatus-esi`, { estatus_esi: selectedEsi });
      toast.success(`Estatus ESI actualizado a "${selectedEsi}"`);
      qc.invalidateQueries({ queryKey: ["ot", ot.id] });
      qc.invalidateQueries({ queryKey: ["audit", ot.id, "esi"] });
      qc.invalidateQueries({ queryKey: ["ots-list"] });
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar estatus ESI");
    } finally {
      setSavingEsi(false);
    }
  };

  const suggestedStatus = useMemo(() => {
    if (summary.totalSkus === 0) return null;
    if (summary.coverage === 0) return "Pendiente de subir";
    if (summary.coverage === 100) return "Subida completa";
    return "Subida parcial";
  }, [summary]);

  return (
    <div className="space-y-4" data-testid="evidencia-status-tab">
      {/* Current status header */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Estatus de Evidencia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Actual</div>
              <EvidenciaStatusBadge status={ot?.estatus_evidencia || "Pendiente de subir"} size="md" data-testid="evidencia-current-status" />
            </div>
            {suggestedStatus && suggestedStatus !== ot?.estatus_evidencia && canChange && (
              <button
                type="button"
                onClick={() => setSelectedStatus(suggestedStatus)}
                className="text-xs text-primary hover:underline text-left sm:text-right"
                data-testid="evidencia-suggested-status"
              >
                Sugerido por cobertura: <span className="font-semibold">{suggestedStatus}</span> →
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard label="Archivos subidos" value={summary.totalFiles} icon={ImageIcon} tone="primary" testId="evidencia-stat-files" />
            <StatCard label="SKUs con evidencia" value={`${summary.withEv} / ${summary.totalSkus}`} icon={CheckCircle2} tone="success" testId="evidencia-stat-covered" />
            <StatCard label="SKUs sin evidencia" value={summary.withoutEv} icon={AlertCircle} tone={summary.withoutEv > 0 ? "warning" : "muted"} testId="evidencia-stat-missing" />
            <StatCard label="Cobertura" value={`${summary.coverage}%`} icon={FileText} tone={summary.coverage === 100 ? "success" : summary.coverage > 0 ? "warning" : "danger"} testId="evidencia-stat-coverage" />
          </div>

          {loadingEv && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Calculando cobertura...
            </div>
          )}

          {/* Change status */}
          <div className="pt-2 border-t border-border">
            {canChange ? (
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
                    Cambiar a
                  </label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="h-11 sm:h-10" data-testid="evidencia-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={save}
                  disabled={saving || selectedStatus === ot?.estatus_evidencia}
                  className="h-11 sm:h-10"
                  data-testid="evidencia-status-save-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Guardar estatus
                </Button>
              </div>
            ) : (
              <div className="p-3 rounded-md bg-muted/40 border border-border text-xs text-muted-foreground">
                Solo lectura. Se requiere permiso <span className="font-mono text-foreground">evidencia_estatus</span> para modificar el estatus de evidencia.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change history (evidencia_status_changed) */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Historial de estatus de evidencia
          </CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <div className="text-xs text-muted-foreground py-6 text-center">Sin cambios registrados aún.</div>
          ) : (
            <ol className="relative border-l-2 border-border ml-2 pl-4 space-y-3" data-testid="evidencia-history-list">
              {audit.map((a) => {
                const ch = (a.changes || [])[0] || {};
                return (
                  <li key={a.id} className="relative">
                    <span className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-primary border-2 border-background" />
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <EvidenciaStatusBadge status={ch.from} size="xs" />
                      <span className="text-muted-foreground">→</span>
                      <EvidenciaStatusBadge status={ch.to} size="xs" />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      <span className="font-mono">{(a.created_at || "").substring(0, 16).replace("T", " ")}</span>
                      <span className="mx-2">•</span>
                      <span>por <span className="text-foreground font-medium">{a.full_name || a.username}</span></span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* ESI status change block */}
      <Card className="bg-card border-border" data-testid="esi-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Estatus ESI
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Actual</div>
              <EsiStatusBadge status={ot?.estatus_esi || "Pendiente revisión"} size="md" data-testid="esi-current-status" />
            </div>
            <p className="text-[11px] text-muted-foreground max-w-md sm:text-right">
              El área ESI confirma que revisó las clasificaciones y envió el reporte al cliente.
            </p>
          </div>

          {/* Change status */}
          <div className="pt-2 border-t border-border">
            {canChangeEsi ? (
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
                    Cambiar a
                  </label>
                  <Select value={selectedEsi} onValueChange={setSelectedEsi}>
                    <SelectTrigger className="h-11 sm:h-10" data-testid="esi-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ESI_STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={saveEsi}
                  disabled={savingEsi || selectedEsi === ot?.estatus_esi}
                  className="h-11 sm:h-10"
                  data-testid="esi-status-save-button"
                >
                  {savingEsi ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Guardar estatus ESI
                </Button>
              </div>
            ) : (
              <div className="p-3 rounded-md bg-muted/40 border border-border text-xs text-muted-foreground">
                Solo lectura. Se requiere permiso <span className="font-mono text-foreground">esi_estatus</span> para modificar el estatus ESI.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change history (esi_status_changed) */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Historial de estatus ESI
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditEsi.length === 0 ? (
            <div className="text-xs text-muted-foreground py-6 text-center">Sin cambios registrados aún.</div>
          ) : (
            <ol className="relative border-l-2 border-border ml-2 pl-4 space-y-3" data-testid="esi-history-list">
              {auditEsi.map((a) => {
                const ch = (a.changes || [])[0] || {};
                return (
                  <li key={a.id} className="relative">
                    <span className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-primary border-2 border-background" />
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <EsiStatusBadge status={ch.from} size="xs" />
                      <span className="text-muted-foreground">→</span>
                      <EsiStatusBadge status={ch.to} size="xs" />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      <span className="font-mono">{(a.created_at || "").substring(0, 16).replace("T", " ")}</span>
                      <span className="mx-2">•</span>
                      <span>por <span className="text-foreground font-medium">{a.full_name || a.username}</span></span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
